import { useId, useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useSetPrcVersionObjectsMutation,
  type PrcVariantInfo,
  type PrcVersion,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { fleetLeading, fleetType, typo } from '@entities/fleet';
import { isEditableStatus, PRC_MAIN_LABEL, prcObjectsLabel, takenObjects } from '@entities/prc';
import { Actions, Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { ObjectsField } from '../ObjectsField';

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  & > .objects {
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

const Editor = styled.form`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  max-width: 640px;

  & > .error {
    margin-bottom: ${({ theme }) => theme.spacing(3)};
  }
`;

/**
 * Объекты версии ПРЦ в шапке: для каких объектов расчёт. Черновик можно
 * перенести на другие объекты; утверждённую и отправленную версию — нет. При
 * вариантах условий — объекты основного расчёта (объекты вариантов заняты).
 */
export function VersionObjects({
  version,
  canWrite,
  variants = [],
}: {
  version: PrcVersion;
  canWrite: boolean;
  variants?: readonly PrcVariantInfo[];
}) {
  const uid = useId();
  const taken = useMemo(
    () => takenObjects(version.objects, variants, null),
    [version.objects, variants],
  );
  const editable = canWrite && isEditableStatus(version.status);
  const [editing, setEditing] = useState(false);
  const [ids, setIds] = useState<string[]>(() => version.objects.map((o) => o.id));
  const [error, setError] = useState<string | null>(null);
  const [save, { isLoading }] = useSetPrcVersionObjectsMutation();

  function start() {
    setIds(version.objects.map((o) => o.id));
    setError(null);
    setEditing(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await save({
        id: version.id,
        project_ids: ids,
        expected_updated_at: version.updated_at,
      }).unwrap();
      setEditing(false);
    } catch (err) {
      setError(apiErrorText(err, 'Объекты не сохранились.'));
    }
  }

  return (
    <>
      <Line>
        <span className="objects">
          {variants.length > 0 && `${PRC_MAIN_LABEL}: `}
          {prcObjectsLabel(version.objects)}
        </span>
        {editable && !editing && (
          <TextButton type="button" onClick={start}>
            Изменить объекты
          </TextButton>
        )}
      </Line>
      {editing && (
        <Editor onSubmit={(e) => void submit(e)} aria-label="Объекты расчёта ПРЦ">
          {error && (
            <div className="error">
              <Notice $tone="danger" role="alert">
                <span className="text">{typo(error)}</span>
              </Notice>
            </div>
          )}
          <ObjectsField
            id={`${uid}-objects`}
            value={ids}
            onChange={setIds}
            disabled={isLoading}
            taken={taken}
          />
          <Actions>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              disabled={isLoading}
              onClick={() => setEditing(false)}
            >
              Отмена
            </Button>
            <Button type="submit" $variant="primary" $size="sm" disabled={isLoading}>
              {isLoading ? 'Сохраняю…' : 'Сохранить объекты'}
            </Button>
          </Actions>
        </Editor>
      )}
    </>
  );
}
